import { getEnvConfig } from '@/lib/config/env'
import { parseApiError } from '@/lib/api/errors'
import { handleApiAuthError } from '@/lib/auth/handlers'
import { readStoredTokens, writeStoredTokens, clearStoredTokens } from '@/lib/auth/tokenStore'
import { Sentry, captureError, isObservabilityEnabled } from '@/lib/observability/sentry'

export type ApiMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

export type ApiRequestOptions = {
  method?: ApiMethod
  headers?: Record<string, string>
  body?: unknown
  auth?: 'include' | 'omit'
  handleAuthErrors?: boolean
  signal?: AbortSignal
  retry?: {
    attempts: number
    backoffMs: number
    retryOn: number[]
  }
  /** Internal — set by the 401-retry path to prevent infinite refresh loops. */
  skipRefreshRetry?: boolean
}

type RefreshTokenResponse = {
  accessToken?: string
  idToken?: string
  tokenType?: string
  expiresIn?: number
}

/**
 * Single in-flight refresh promise. When a 401 lands on N concurrent requests
 * they all await the same /auth/refresh fetch instead of stampeding Cognito.
 */
let refreshInflight: Promise<boolean> | null = null

async function refreshAccessTokenViaCookie(): Promise<boolean> {
  if (refreshInflight) return refreshInflight
  const { apiBaseUrl } = getEnvConfig()
  refreshInflight = (async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      })
      if (!response.ok) {
        clearStoredTokens()
        return false
      }
      const payload = (await response.json()) as RefreshTokenResponse
      if (!payload?.accessToken) {
        clearStoredTokens()
        return false
      }
      const expiresIn = Number(payload.expiresIn)
      writeStoredTokens({
        accessToken: payload.accessToken,
        idToken: payload.idToken,
        tokenType: payload.tokenType,
        expiresAt: Number.isFinite(expiresIn) && expiresIn > 0 ? Date.now() + expiresIn * 1000 : undefined,
      })
      return true
    } catch {
      clearStoredTokens()
      return false
    } finally {
      refreshInflight = null
    }
  })()
  return refreshInflight
}

function isRetryEligible(method: string): boolean {
  return method.toUpperCase() === 'GET'
}

const RETRY_BACKOFF_CAP_MS = 30_000

/**
 * Exponential backoff with full jitter (security-baseline §2.11).
 *
 * Linear backoff (baseMs * attempt) makes many clients retry in lockstep
 * against an already-throttled Lambda — billing-DoS and recovery-stall
 * vector. Full jitter spreads retries uniformly across the growing window:
 *   attempt 1 → [0, baseMs)
 *   attempt 2 → [0, 2 * baseMs)
 *   attempt 3 → [0, 4 * baseMs)
 *   ... capped at RETRY_BACKOFF_CAP_MS.
 *
 * Exported for tests / call sites that need to inspect timing.
 */
export function computeRetryBackoffMs(baseMs: number, attempt: number): number {
  const safeBase = Number.isFinite(baseMs) && baseMs > 0 ? baseMs : 0
  const safeAttempt = Number.isFinite(attempt) && attempt > 0 ? Math.floor(attempt) : 1
  if (safeBase === 0) return 0
  const ceiling = Math.min(RETRY_BACKOFF_CAP_MS, safeBase * 2 ** (safeAttempt - 1))
  return Math.floor(Math.random() * ceiling)
}

function statusFromError(error: unknown): number | undefined {
  if (typeof error !== 'object' || !error) return undefined
  const status = (error as { status?: unknown }).status
  return typeof status === 'number' ? status : undefined
}

async function readResponseBody<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T
  }
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    return undefined as T
  }
  return (await response.json()) as T
}

/**
 * In-flight GET dedup (security-baseline / CLAUDE.md: "concurrent reads for the
 * same key share one request"). When N components request the same URL while
 * the first is still pending — page mount fan-out, a reload firing over an
 * in-progress load, React StrictMode double-invoke — they all await the same
 * promise instead of stampeding an already-stressed backend. Keyed by
 * `GET <absolute-url>`; the cleared-on-settle map means only *overlapping*
 * reads coalesce, never sequential ones (so data never goes stale).
 */
const inflightGets = new Map<string, Promise<unknown>>()

export function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase()

  // Only idempotent GETs are safe to coalesce — never POST/PATCH/PUT/DELETE.
  // Skip dedup when the caller owns an AbortSignal: a shared fetch would let
  // one caller's abort cancel the response other callers are awaiting.
  if (method !== 'GET' || options.signal) {
    return executeRequest<T>(path, options)
  }

  const { apiBaseUrl } = getEnvConfig()
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const key = `GET ${apiBaseUrl}${normalizedPath}`

  const existing = inflightGets.get(key)
  if (existing) return existing as Promise<T>

  const promise = executeRequest<T>(path, options)
  inflightGets.set(key, promise)
  // `.then(cleanup, cleanup)` and NOT `.finally(cleanup)`: `finally` returns a
  // DERIVED promise that re-rejects with the same reason and that nobody awaits,
  // so every refused GET raised a second, unhandled rejection alongside the one
  // the caller was already handling correctly. Passing an onRejected handler
  // consumes it here; the caller still gets the original `promise` intact.
  const forget = () => {
    if (inflightGets.get(key) === promise) inflightGets.delete(key)
  }
  void promise.then(forget, forget)
  return promise
}

async function executeRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const { apiBaseUrl } = getEnvConfig()
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const method = options.method ?? 'GET'
  const includeAuth = options.auth !== 'omit'
  const tokens = includeAuth ? readStoredTokens() : null

  const headers: Record<string, string> = {
    ...options.headers,
  }
  if (options.body !== undefined) {
    headers['Content-Type'] = headers['Content-Type'] ?? 'application/json'
  }

  if (includeAuth && tokens?.accessToken) {
    headers.Authorization = `Bearer ${tokens.accessToken}`
  }
  if (includeAuth && tokens?.idToken) {
    headers['x-cognito-id-token'] = tokens.idToken
  }

  const shouldRetry = Boolean(options.retry) && isRetryEligible(method)
  const maxAttempts = shouldRetry ? Math.max(1, options.retry!.attempts) : 1
  let attempt = 0

  const runRequest = async () => {
    while (attempt < maxAttempts) {
      attempt += 1
      const response = await fetch(`${apiBaseUrl}${normalizedPath}`, {
        method,
        headers,
        body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
        signal: options.signal,
        // Required so the HttpOnly refresh-cookie travels on /auth/* and so
        // any future cookie-based session features keep working. Origin
        // allowlist on the backend (security-baseline §1.4) prevents foreign
        // sites from abusing this.
        credentials: 'include',
      })

      // Silent refresh on 401: try once via the HttpOnly refresh cookie, then
      // re-issue the original request with a fresh access token. Skipped when
      // auth is omitted, when the caller already retried, or when the request
      // is itself the refresh / login endpoint (would loop).
      const isAuthEndpoint = normalizedPath.startsWith('/auth/')
      if (
        response.status === 401 &&
        includeAuth &&
        !options.skipRefreshRetry &&
        !isAuthEndpoint
      ) {
        const refreshed = await refreshAccessTokenViaCookie()
        if (refreshed) {
          const refreshedTokens = readStoredTokens()
          if (refreshedTokens?.accessToken) {
            headers.Authorization = `Bearer ${refreshedTokens.accessToken}`
          }
          if (refreshedTokens?.idToken) {
            headers['x-cognito-id-token'] = refreshedTokens.idToken
          }
          // Reset attempt counter for a clean replay; mark to avoid loops.
          options = { ...options, skipRefreshRetry: true }
          attempt = 0
          continue
        }
      }

      if (!response.ok) {
        if (shouldRetry && attempt < maxAttempts && options.retry!.retryOn.includes(response.status)) {
          const timeout = computeRetryBackoffMs(options.retry!.backoffMs, attempt)
          await new Promise((resolve) => setTimeout(resolve, timeout))
          continue
        }

        const error = await parseApiError(response)
        captureError(error, {
          api_method: method,
          api_path: normalizedPath,
          api_status: String(response.status),
        })
        if (options.handleAuthErrors !== false) {
          const result = handleApiAuthError(error, {
            unauthorizedRedirectPath: '/login',
          })
          if (result === 'forbidden' && typeof window !== 'undefined') {
            window.location.assign('/forbidden')
          }
        }
        throw error
      }

      return await readResponseBody<T>(response)
    }

    throw new Error('API request retry attempts exhausted.')
  }

  if (!isObservabilityEnabled()) {
    return await runRequest()
  }

  return await Sentry.startSpan(
    {
      op: 'http.client',
      name: `${method} ${normalizedPath}`,
      attributes: {
        'http.request.method': method,
        'url.path': normalizedPath,
      },
    },
    async (span) => {
      try {
        const result = await runRequest()
        span?.setAttribute('http.response.status_code', 200)
        return result
      } catch (error) {
        const status = statusFromError(error)
        if (status !== undefined) {
          span?.setAttribute('http.response.status_code', status)
        }
        throw error
      }
    },
  )
}
